// PawCita API client. Talks to the FastAPI backend. Only the auth token is
// kept in localStorage; all business data lives in MongoDB via the backend.
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pawcita:token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setToken = (t) => {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {}
  window.dispatchEvent(new Event("pawcita-session-changed"));
};
export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
  window.dispatchEvent(new Event("pawcita-session-changed"));
};

async function request(path, { method = "GET", body, headers: extraHeaders } = {}) {
  const headers = { "Content-Type": "application/json", ...(extraHeaders || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const detail = data && data.detail;
    const detailCode =
      detail && typeof detail === "object" && !Array.isArray(detail)
        ? detail.code
        : null;
    const codeMessages = {
      invalid_country: "El país seleccionado no está disponible en PawCita.",
      country_required: "Selecciona un país para continuar.",
      invalid_countries: "La selección de países no es válida.",
      countries_required: "Selecciona al menos un país.",
    };
    const msg =
      typeof detail === "string"
        ? detail
        : detailCode && codeMessages[detailCode]
        ? codeMessages[detailCode]
        : Array.isArray(detail)
        ? "Revisa los datos: el correo debe ser válido y la contraseña tener al menos 6 caracteres."
        : "Ocurrió un error. Intenta de nuevo.";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const transactionValidationApi = {
  validate: (token) =>
    request(`/ally/transactions/validate/${encodeURIComponent(token)}`),

  complete: (token) =>
    request(
      `/ally/transactions/validate/${encodeURIComponent(token)}/complete`,
      { method: "POST" }
    ),
};

export const benefitValidationApi = {
  validate: (identifier) =>
    request(`/ally/benefits/validate/${encodeURIComponent(identifier)}`),

  complete: (identifier) =>
    request(
      `/ally/benefits/validate/${encodeURIComponent(identifier)}/complete`,
      { method: "POST" }
    ),
};


export const allyPortalApi = {
  invitation: (token) =>
    request("/ally/invitations/inspect", {
      method: "POST",
      body: { token },
    }),

  acceptInvitation: (token, password) =>
    request("/ally/invitations/accept", {
      method: "POST",
      body: { token, password },
    }),

  dashboard: () =>
    request("/ally/dashboard"),

  updateProfile: (body) =>
    request("/ally/profile", {
      method: "PUT",
      body,
    }),

  benefits: ({ status = "", search = "" } = {}) => {
    const params = new URLSearchParams();

    if (status) params.set("status", status);
    if (search) params.set("search", search);

    const qs = params.toString();

    return request(`/ally/benefits${qs ? `?${qs}` : ""}`);
  },

  transactions: ({ status = "", search = "" } = {}) => {
    const params = new URLSearchParams();

    if (status) params.set("status", status);
    if (search) params.set("search", search);

    const qs = params.toString();

    return request(`/ally/transactions${qs ? `?${qs}` : ""}`);
  },

  reviews: () =>
    request("/ally/reviews"),

  replyReview: (reviewId, text) =>
    request(`/ally/reviews/${encodeURIComponent(reviewId)}/reply`, {
      method: "PUT",
      body: { text },
    }),
};


export const api = {
  auth: {
    register: (payload) => request("/auth/register", { method: "POST", body: payload }),
    login: (payload) => request("/auth/login", { method: "POST", body: payload }),
    logout: () => request("/auth/logout", { method: "POST" }),
    me: () => request("/auth/me"),
    updateProfile: (payload) => request("/auth/me", { method: "PUT", body: payload }),
    verifyEmail: (code) => request("/auth/verify-email", { method: "POST", body: { code } }),
    resendVerification: () => request("/auth/resend-verification", { method: "POST" }),
    forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", body: { email } }),
    resetPassword: (email, code, new_password) =>
      request("/auth/reset-password", { method: "POST", body: { email, code, new_password } }),
  },
  dogs: {
    list: () => request("/dogs"),
    create: (payload) => request("/dogs", { method: "POST", body: payload }),
    update: (id, payload) => request(`/dogs/${id}`, { method: "PUT", body: payload }),
    remove: (id) => request(`/dogs/${id}`, { method: "DELETE" }),
    uploadPhoto: async (id, file) => {
      const form = new FormData();
      form.append("file", file);
      const token = getToken();
      const res = await fetch(`${BASE}/api/dogs/${id}/photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (!res.ok) {
        throw new Error((data && data.detail) || "No se pudo subir la foto.");
      }
      return data;
    },
  },
  breeds: {
    list: () => request("/breeds"),
  },
  explore: {
    list: () => request("/explore"),
  },
  requests: {
    send: (targetDogId, message, fromDogId = null) =>
      request("/requests", {
        method: "POST",
        body: {
          target_dog_id: targetDogId,
          from_dog_id: fromDogId,
          message,
        },
      }),
    sent: () => request("/requests/sent"),
    received: () => request("/requests/received"),
    setStatus: (id, status) =>
      request(`/requests/${id}/status`, { method: "PUT", body: { status } }),
  },
  chats: {
    list: () => request("/chats"),
    sendMessage: (chatId, text) =>
      request(`/chats/${chatId}/messages`, { method: "POST", body: { text } }),
    setAppointment: (chatId, appointment) =>
      request(`/chats/${chatId}/appointment`, { method: "PUT", body: { appointment } }),
    read: (chatId) => request(`/chats/${chatId}/read`, { method: "POST" }),
  },
  messages: {
    unreadCount: () => request("/messages/unread-count"),
  },
  push: {
    publicKey: () => request("/push/public-key"),
    status: () => request("/push/status"),
    subscribe: (sub) => request("/push/subscribe", { method: "POST", body: sub }),
    unsubscribe: (endpoint) => request("/push/unsubscribe", { method: "POST", body: { endpoint, keys: {} } }),
  },
  admin: {
    stats: () => request("/admin/stats"),
    moderation: () => request("/admin/moderation-events"),
    users: (search = "", page = 1) =>
      request(`/admin/users?search=${encodeURIComponent(search)}&page=${page}`),
    userDetail: (id) => request(`/admin/users/${id}`),
    setUserStatus: (id, active) =>
      request(`/admin/users/${id}/status`, { method: "PUT", body: { active } }),

    allyReviews: ({ status = "", reportedOnly = false } = {}) => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (reportedOnly) params.set("reported_only", "true");
      const qs = params.toString();
      return request(`/admin/ally-reviews${qs ? `?${qs}` : ""}`);
    },

    setAllyReviewStatus: (id, status) =>
      request(`/admin/ally-reviews/${id}/status`, {
        method: "PUT",
        body: { status },
      }),

    organizations: (search = "", countryCode) =>
      request(`/admin/organizations?search=${encodeURIComponent(search)}${countryCode ? `&country_code=${encodeURIComponent(countryCode)}` : ""}`),

    createOrganization: (body) =>
      request("/admin/organizations", { method: "POST", body }),

    updateOrganization: (id, body) =>
      request(`/admin/organizations/${id}`, { method: "PUT", body }),

    setOrganizationStatus: (id, active) =>
      request(`/admin/organizations/${id}/status`, {
        method: "PUT",
        body: { active },
      }),

    deleteOrganization: (id) =>
      request(`/admin/organizations/${id}`, { method: "DELETE" }),

    uploadOrganizationLogo: async (id, file) => {
      const form = new FormData();
      form.append("file", file);

      const token = getToken();

      const res = await fetch(`${BASE}/api/admin/organizations/${id}/logo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          (data && data.detail) || "No se pudo subir el logo de la organización."
        );
      }

      return data;
    },

    banners: (countryCode) =>
      request(`/admin/banners${countryCode ? `?country_code=${encodeURIComponent(countryCode)}` : ""}`),

    bannerMetrics: (id) =>
      request(`/admin/banners/${id}/metrics`),

    createBanner: (body) =>
      request("/admin/banners", {
        method: "POST",
        body,
      }),

    updateBanner: (id, body) =>
      request(`/admin/banners/${id}`, {
        method: "PUT",
        body,
      }),

    setBannerStatus: (id, active) =>
      request(`/admin/banners/${id}/status`, {
        method: "PUT",
        body: { active },
      }),

    deleteBanner: (id) =>
      request(`/admin/banners/${id}`, {
        method: "DELETE",
      }),

    uploadBannerPhoto: async (id, file) => {
      const form = new FormData();
      form.append("file", file);

      const token = getToken();

      const res = await fetch(
        `${BASE}/api/admin/banners/${id}/photo`,
        {
          method: "POST",
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : {},
          body: form,
        }
      );

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          (data && data.detail) ||
            "No se pudo subir la imagen del banner."
        );
      }

      return data;
    },

    contentCards: (countryCode) =>
      request(`/admin/content/cards${countryCode ? `?country_code=${encodeURIComponent(countryCode)}` : ""}`),

    createContentCard: (body) =>
      request("/admin/content/cards", {
        method: "POST",
        body,
      }),

    updateContentCard: (id, body) =>
      request(`/admin/content/cards/${id}`, {
        method: "PUT",
        body,
      }),

    setContentCardStatus: (id, active) =>
      request(`/admin/content/cards/${id}/status`, {
        method: "PUT",
        body: { active },
      }),

    deleteContentCard: (id) =>
      request(`/admin/content/cards/${id}`, {
        method: "DELETE",
      }),

    uploadContentCardPhoto: async (id, file) => {
      const form = new FormData();
      form.append("file", file);

      const token = getToken();

      const res = await fetch(
        `${BASE}/api/admin/content/cards/${id}/photo`,
        {
          method: "POST",
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : {},
          body: form,
        }
      );

      let data = null;

      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          (data && data.detail) ||
            "No se pudo subir la imagen del contenido."
        );
      }

      return data;
    },

    deleteContentCardPhoto: (id) =>
      request(`/admin/content/cards/${id}/photo`, {
        method: "DELETE",
      }),

    dogs: (search = "", page = 1) =>
      request(`/admin/dogs?search=${encodeURIComponent(search)}&page=${page}`),
    dogDetail: (id) => request(`/admin/dogs/${id}`),
    setDogStatus: (id, active) =>
      request(`/admin/dogs/${id}/status`, { method: "PUT", body: { active } }),
    allies: (search = "", countryCode) =>
      request(`/admin/allies?search=${encodeURIComponent(search)}${countryCode ? `&country_code=${encodeURIComponent(countryCode)}` : ""}`),
    createAlly: (body) => request("/admin/allies", { method: "POST", body }),
    updateAlly: (id, body) => request(`/admin/allies/${id}`, { method: "PUT", body }),
    inviteAlly: (id, body = {}) =>
      request(`/admin/allies/${id}/invite`, { method: "POST", body }),
    geocodeAlly: (body) => request("/admin/allies/geocode", { method: "POST", body }),
    setAllyStatus: (id, active) =>
      request(`/admin/allies/${id}/status`, { method: "PUT", body: { active } }),
    activities: (countryCode) => request(`/admin/activities${countryCode ? `?country_code=${encodeURIComponent(countryCode)}` : ""}`),
    createActivity: (body) => request("/admin/activities", { method: "POST", body }),
    updateActivity: (id, body) => request(`/admin/activities/${id}`, { method: "PUT", body }),
    setActivityStatus: (id, active) =>
      request(`/admin/activities/${id}/status`, { method: "PUT", body: { active } }),
    activityParticipants: (id) => request(`/admin/activities/${id}/participants`),
    activityChat: (id) => request(`/admin/activities/${id}/chat`),
    sendActivityChat: (id, text) => request(`/admin/activities/${id}/chat/messages`, { method: "POST", body: { text } }),
    uploadActivityPhoto: async (id, file) => {
      const form = new FormData();
      form.append("file", file);
      const token = getToken();
      const res = await fetch(`${BASE}/api/admin/activities/${id}/photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) throw new Error((data && data.detail) || "No se pudo subir la imagen.");
      return data;
    },
    allyMetrics: (id) => request(`/admin/allies/${id}/metrics`),
    sosAlerts: (status = "") => request(`/admin/sos/alerts?status=${encodeURIComponent(status)}`),
    setSosStatus: (id, status) => request(`/admin/sos/alerts/${id}/status`, { method: "PUT", body: { status } }),
    sosSightings: (id) => request(`/admin/sos/alerts/${id}/sightings`),
    setSosSightingStatus: (id, status) => request(`/admin/sos/sightings/${id}/status`, { method: "PUT", body: { status } }),
    uploadAllyPhoto: async (id, file) => {
      const form = new FormData();
      form.append("file", file);
      const token = getToken();
      const res = await fetch(`${BASE}/api/admin/allies/${id}/photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (!res.ok) {
        throw new Error((data && data.detail) || "No se pudo subir la foto.");
      }
      return data;
    },
  },
  allies: {
    list: (category = "", search = "") =>
      request(`/allies?category=${encodeURIComponent(category)}&search=${encodeURIComponent(search)}`),
    detail: (id) => request(`/allies/${id}`),
    event: (id, event_type) =>
      request(`/allies/${id}/events`, { method: "POST", body: { event_type } }),
  },
  veterinaries: {
    list: (params = {}) => {
      const q = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "" && v !== false) q.append(k, v);
      });
      const qs = q.toString();
      return request(`/veterinaries${qs ? `?${qs}` : ""}`);
    },
    detail: (id) => request(`/allies/${id}`),
  },
  activities: {
    list: (category = "") =>
      request(`/activities?category=${encodeURIComponent(category)}`),
    detail: (id) => request(`/activities/${id}`),
    myParticipation: (id) => request(`/activities/${id}/my-participation`),
    join: (id, dog_id) => request(`/activities/${id}/join`, { method: "POST", body: { dog_id } }),
    leave: (id) => request(`/activities/${id}/leave`, { method: "PUT" }),
    chat: (id) => request(`/activities/${id}/chat`),
    sendChat: (id, text) => request(`/activities/${id}/chat/messages`, { method: "POST", body: { text } }),
    my: () => request(`/activities/my`),
    activityChats: () => request(`/activity-chats`),
  },
  sos: {
    list: () => request("/sos/alerts"),    detail: (id) => request(`/sos/alerts/${id}`),
    create: (payload) => request("/sos/alerts", { method: "POST", body: payload }),
    myAlerts: () => request("/sos/my-alerts"),
    markFound: (id) => request(`/sos/alerts/${id}/found`, { method: "PUT" }),
    sightings: (id) => request(`/sos/alerts/${id}/sightings`),
    addSighting: (id, payload) => request(`/sos/alerts/${id}/sightings`, { method: "POST", body: payload }),
    uploadSightingPhoto: async (sightingId, file) => {
      const form = new FormData();
      form.append("file", file);
      const token = getToken();
      const res = await fetch(`${BASE}/api/sos/sightings/${sightingId}/photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) throw new Error((data && data.detail) || "No se pudo subir la foto.");
      return data;
    },
    chats: (id) => request(`/sos/alerts/${id}/chat`),
    startChat: (id) => request(`/sos/alerts/${id}/chat`, { method: "POST" }),
    chatMessages: (chatId) => request(`/sos/chats/${chatId}/messages`),
    sendChatMessage: (chatId, text) => request(`/sos/chats/${chatId}/messages`, { method: "POST", body: { text } }),
    chatFromSighting: (sightingId) => request(`/sos/sightings/${sightingId}/chat`, { method: "POST" }),
  },
  users: {
    setLocation: (lat, lng) => request("/users/location", { method: "POST", body: { lat, lng } }),
    locationStatus: () => request("/users/location/status"),
  },
  pawcitaId: {
    publicProfile: (token) =>
      request(`/pawcita-id/${encodeURIComponent(token)}`),

    reportFound: (token, body) =>
      request(`/pawcita-id/${encodeURIComponent(token)}/found`, {
        method: "POST",
        body,
      }),

    // Chat anónimo del reportante. El chat_token se envía SOLO por header
    // (nunca en la URL) y jamás se persiste en el backend en claro.
    getReportChat: (chatToken) =>
      request("/pawcita-id/report-chat", {
        headers: { "X-Pawcita-Chat-Token": chatToken },
      }),

    sendReportChatMessage: (chatToken, text) =>
      request("/pawcita-id/report-chat/messages", {
        method: "POST",
        body: { text },
        headers: { "X-Pawcita-Chat-Token": chatToken },
      }),
  },

  passport: {
    getHealth: (dogId) => request(`/dogs/${dogId}/health`),
    saveHealth: (dogId, body) => request(`/dogs/${dogId}/health`, { method: "PUT", body }),
    records: (dogId) => request(`/dogs/${dogId}/medical-records`),
    createRecord: (dogId, body) => request(`/dogs/${dogId}/medical-records`, { method: "POST", body }),
    updateRecord: (dogId, recordId, body) =>
      request(`/dogs/${dogId}/medical-records/${recordId}`, { method: "PUT", body }),
    deleteRecord: (dogId, recordId) =>
      request(`/dogs/${dogId}/medical-records/${recordId}`, { method: "DELETE" }),
    confirmRecords: (dogId, body) =>
      request(`/dogs/${dogId}/medical-records/confirm`, { method: "POST", body }),
    documents: (dogId) => request(`/dogs/${dogId}/health-documents`),
    cartilla: (dogId) => request(`/dogs/${dogId}/cartilla`),
    reorderCartilla: (dogId, groupId, order) =>
      request(`/dogs/${dogId}/cartilla/${groupId}/reorder`, { method: "POST", body: { order } }),
    pdfBlob: async (dogId, groupId, { signal } = {}) => {
      const token = getToken();
      const controller = new AbortController();
      let invalid = false;
      const invalidate = () => { invalid = true; controller.abort(); };
      const storage = event => {
        if (event.key === null || event.key === TOKEN_KEY) invalidate();
      };
      const check = () => {
        if (!token || invalid || signal?.aborted || getToken() !== token)
          throw new Error("La sesión cambió.");
      };
      window.addEventListener("pawcita-session-changed", invalidate);
      window.addEventListener("storage", storage);
      signal?.addEventListener("abort", invalidate);
      try {
        check();
        const res = await fetch(`${BASE}/api/dogs/${dogId}/cartilla/${groupId}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal, cache: "no-store", redirect: "error",
        });
        check();
        if (!res.ok) throw new Error("No se pudo generar el PDF.");
        const blob = await res.blob();
        check();
        return blob;
      } finally {
        window.removeEventListener("pawcita-session-changed", invalidate);
        window.removeEventListener("storage", storage);
        signal?.removeEventListener("abort", invalidate);
      }
    },
    deleteDocument: (dogId, docId) =>
      request(`/dogs/${dogId}/health-documents/${docId}`, { method: "DELETE" }),
    analyzeDocument: (dogId, docId, force) =>
      request(`/dogs/${dogId}/health-documents/${docId}/analyze${force ? "?force=true" : ""}`, { method: "POST" }),
    analysisStatus: (dogId, docId) =>
      request(`/dogs/${dogId}/health-documents/${docId}/analysis-status`),
    uploadDocument: async (dogId, file, groupId) => {
      const form = new FormData();
      form.append("file", file);
      if (groupId) form.append("document_group_id", groupId);
      const token = getToken();
      const res = await fetch(`${BASE}/api/dogs/${dogId}/health-documents`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) throw new Error((data && data.detail) || "No se pudo subir el documento.");
      return data;
    },
  },
};

export default api;
